import { handleApiError } from "./utils";
import { PUBLIC_ENDPOINTS } from "./endpoints";
import { SERVER_URL } from "../../../../../../ServerLink";
import axios from "axios";

/**
 * Helper to build full URL with query params
 * @param {string} endpoint 
 * @param {Object} [params={}] 
 * @returns {string}
 */
const buildUrl = (endpoint, params = {}) => {
    const url = new URL(`${SERVER_URL}${endpoint}`);

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            url.searchParams.append(key, String(value));
        }
    });

    return url.toString();
};

/**
 * Centralized response handler with better error extraction
 * @param {Response} response 
 * @param {string} defaultMessage 
 * @returns {Promise<any>}
 * @throws {Error}
 */
const handleResponse = async (response, defaultMessage) => {
    if (!response.ok) {
        let errorData = { message: defaultMessage };
        try {
            errorData = await response.json();
        } catch {
            // silent fail - use default
        }

        const error = new Error(errorData?.message || defaultMessage);
        error.status = response.status;
        throw error;
    }

    try {
        return await response.json();
    } catch {
        return { success: true }; // for endpoints that return no JSON body
    }
};

/**
 * Validate invitation token
 * GET /api/v2/incident-reports/public/invite/validate?token=...
 * 
 * @param {string} token 
 * @returns {Promise<any>} validated invitation data
 */
export const validateInvitationToken = async (token) => {
    try {
        if (!token) throw new Error("Token is required");

        const url = buildUrl(PUBLIC_ENDPOINTS.VALIDATE, { token });

        const res = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "omit", // usually not needed for public endpoints
        });

        const data = await handleResponse(res, "Invalid or expired invitation");
        return data?.data || data;
    } catch (error) {
        handleApiError(error, "Failed to validate invitation");
        throw error; // re-throw so caller can handle
    }
};

/**
 * Submit public incident report
 * POST /api/v2/incident-reports/public/submit
 * 
 * @param {Object} payload 
 * @param {string} payload.token 
 * @param {Object} payload.reportData 
 * @returns {Promise<any>}
 */
export const submitPublicIncidentReport = async (payload) => {
    try {
        if (!payload?.token || !payload?.reportData) {
            throw new Error("Token and reportData are required");
        }

        const url = `${SERVER_URL}${PUBLIC_ENDPOINTS.SUBMIT}`;

        const res = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
            credentials: "omit",
        });

        const data = await handleResponse(res, "Failed to submit incident report");
        return data?.data || data;
    } catch (error) {
        handleApiError(error, "Failed to submit incident report");
        throw error;
    }
};

/**
 * Request presigned S3 PUT URL for uploading files (public flow)
 * POST /api/v2/incident-reports/public/upload/request
 * 
 * @param {Object} payload 
 * @param {string} payload.token 
 * @param {string} payload.fileName 
 * @param {string} payload.contentType 
 * @returns {Promise<{ presignedUrl: string, key: string }>}
 */
export const requestPublicUploadUrl = async (payload) => {
    try {
        if (!payload?.token || !payload?.fileName || !payload?.contentType) {
            throw new Error("Token, fileName, and contentType are required");
        }

        const url = `${SERVER_URL}${PUBLIC_ENDPOINTS.UPLOAD_REQUEST}`;

        const res = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
            credentials: "omit",
        });

        const data = await handleResponse(res, "Failed to get upload URL");
        return data?.data || data;
    } catch (error) {
        handleApiError(error, "Failed to request upload URL");
        throw error;
    }
};

/**
 * Request presigned S3 GET URL for previewing / downloading files
 * POST /api/v2/incident-reports/public/preview/request
 * 
 * @param {Object} payload 
 * @param {string} payload.token 
 * @param {string} payload.key - S3 object key
 * @returns {Promise<{ presignedUrl: string, key: string }>}
 */
export const requestPublicPreviewUrl = async (payload) => {
    try {
        if (!payload?.token || !payload?.key) {
            throw new Error("Token and key are required for preview");
        }

        const url = `${SERVER_URL}${PUBLIC_ENDPOINTS.PREVIEW_REQUEST || '/preview/request'}`;

        const res = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
            credentials: "omit",
        });

        const data = await handleResponse(res, "Failed to get preview URL");
        return data?.data || data;
    } catch (error) {
        handleApiError(error, "Failed to generate preview URL");
        throw error;
    }
};

/**
 * Get full incident report preview (with signed attachment URLs)
 * GET /api/v2/incident-reports/public/preview?token=...
 * 
 * @param {string} token
 * @returns {Promise<{
 *   report: any,
 *   attachments: {
 *     leaseDocument: string | null,
 *     insuranceDocument: string | null,
 *     incidentAttachments: Array<{ key: string, url: string | null }>,
 *     rootAttachments: Array<{ key: string, url: string | null }>
 *   },
 *   previewToken: string,
 *   expiresAt: string | null
 * }>}
 */
export const getPublicIncidentPreview = async (token) => {
    try {
        if (!token) {
            throw new Error("Token is required for preview");
        }

        const url = buildUrl(
            PUBLIC_ENDPOINTS.PREVIEW || 'v2/incident-reports/public/preview',
            { token }
        );

        const res = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "omit",
        });

        const data = await handleResponse(res, "Failed to load incident preview");

        return data?.data || data;
    } catch (error) {
        handleApiError(error, "Failed to fetch incident preview");
        throw error;
    }
};

/**
 * Download incident report PDF using a public preview token (no authentication)
 * GET /v2/incident-reports/public/pdf?token=...&timezone=...
 * 
 * @param {string} token - preview token from email
 * @param {string} [timezone] - optional IANA timezone
 * @returns {Promise<void>}
 */
export const downloadIncidentPDFWithToken = async (token, timezone) => {
  try {
    if (!token) throw new Error("Token is required");

    const params = new URLSearchParams({ token });
    if (timezone) params.set("timezone", timezone);

    const url = `${SERVER_URL}${PUBLIC_ENDPOINTS.PDF_DOWNLOAD}?${params.toString()}`;

    const res = await fetch(url, { method: "GET" });

    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.message || `Failed to download PDF (${res.status})`);
    }

    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    const cd = res.headers.get("content-disposition") || "";
    const match = cd.match(/filename="?([^";\n]+)"?/);
    link.download = match ? match[1] : `incident_preview.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(link.href);
  } catch (error) {
    handleApiError(error, "Failed to download PDF");
  }
};